import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from 'src/api/axiosInstance';

// ------------------------------------------------
// ASYNC THUNK — validate the current tenant's status
// The backend resolves the tenant from the subdomain automatically
// via TenantContext, so no query-param is needed.
// Endpoint: GET /rest/tenant/status
// Response: { status: "ACTIVE" | "SUSPENDED" | "NOT_FOUND", tenantName?, subdomain? }
// ------------------------------------------------
export const validateTenantStatus = createAsyncThunk(
  'tenantValidation/validate',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/rest/tenant/status');
      return response.data; // { status, tenantName?, subdomain? }
    } catch (error) {
      // If the backend returns a 404, treat the tenant as not found
      if (error?.response?.status === 404) {
        return rejectWithValue({ status: 'NOT_FOUND' });
      }
      return rejectWithValue({
        status: 'ERROR',
        message: error?.response?.data?.message || error.message,
      });
    }
  }
);

// ------------------------------------------------
// SLICE
// ------------------------------------------------
const tenantValidationSlice = createSlice({
  name: 'tenantValidation',
  initialState: {
    /** 'idle' | 'loading' | 'active' | 'not-found' | 'suspended' | 'error' */
    status: 'idle',
    tenantName: null,
    subdomain: null,
    error: null,
  },
  reducers: {
    resetTenantValidation: (state) => {
      state.status = 'idle';
      state.tenantName = null;
      state.subdomain = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateTenantStatus.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(validateTenantStatus.fulfilled, (state, action) => {
        const data = action.payload;
        const backendStatus = (data?.status || '').toUpperCase();

        if (backendStatus === 'ACTIVE') {
          state.status = 'active';
        } else if (backendStatus === 'SUSPENDED') {
          state.status = 'suspended';
        } else {
          state.status = 'not-found';
        }

        state.tenantName = data?.tenantName || null;
        state.subdomain = data?.subdomain || null;
      })
      .addCase(validateTenantStatus.rejected, (state, action) => {
        const payload = action.payload;
        if (payload?.status === 'NOT_FOUND') {
          state.status = 'not-found';
        } else {
          state.status = 'error';
          state.error = payload?.message || 'Failed to validate tenant';
        }
      });
  },
});

export const { resetTenantValidation } = tenantValidationSlice.actions;
export default tenantValidationSlice.reducer;
